import { CellDoubleClickedEvent, CellEditingStartedEvent, CellEditingStoppedEvent, CellSelectionOptions, ColDef, ColGroupDef, DoesExternalFilterPass, ExcelExportParams, FilterChangedEvent, FirstDataRenderedEvent, GridOptions, IsExternalFilterPresent, IsRowMaster, RowClassParams, RowEditingStoppedEvent, RowGroupingDisplayType, RowSelectionOptions, RowValueChangedEvent, SelectionChangedEvent } from 'ag-grid-community';
import moment from 'moment';
import { Subject } from 'rxjs';

export const PxdAgGridDefaultColumnDef: any = {
  filter: true,
  sortable: true,
  resizable: true,
  enableValue: true,
  enableRowGroup: true,
  enablePivot: true,
  menuTabs: ['generalMenuTab', 'filterMenuTab', 'columnsMenuTab'],
  filterParams: {
    includeBlanksInEquals: true,
    includeBlanksInLessThan: true,
    includeBlanksInGreaterThan: true,
    includeBlanksInRange: true,
    excelMode: 'windows',
  }
};

export const PxdAgAutoHeightGridDefaultColumnDef: any = {
  filter: true,
  sortable: true,
  resizable: true,
  enableValue: true,
  enableRowGroup: true,
  wrapText: true,
  autoHeight: true,
  enablePivot: true,
  menuTabs: ['generalMenuTab', 'filterMenuTab', 'columnsMenuTab'],
  filterParams: {
    includeBlanksInEquals: true,
    includeBlanksInLessThan: true,
    includeBlanksInGreaterThan: true,
    includeBlanksInRange: true,
    excelMode: 'windows',
  }
};

export const PxdAgEditGridDefaultColumnDef: any = {
  filter: true,
  sortable: true,
  resizable: true,
  enableValue: true,
  enableRowGroup: true,
  enablePivot: true,
  editable: true,
  menuTabs: ['generalMenuTab', 'filterMenuTab', 'columnsMenuTab'],
  filterParams: {
    includeBlanksInEquals: true,
    includeBlanksInLessThan: true,
    includeBlanksInGreaterThan: true,
    includeBlanksInRange: true,
    excelMode: 'windows',
  },
  comparator: (a, b, nodeA, nodeB, isInverted) => {
    if (a === b) {
      return 0;
    }
    else if (a === undefined || a === 'null' || a === null || a === "") {
      return isInverted ? -1 : 1;
    }
    else if (b === undefined || b === 'null' || b === null || b === "") {
      return isInverted ? 1 : -1;
    }
    else {
      if (a == b) return 0;
      return (a > b) ? 1 : -1;
    }

  }
};

export const PxdAgGridStatusBarOptions: any = {
  statusPanels: [
    {
      statusPanel: 'agTotalAndFilteredRowCountComponent',
      align: 'left',
    },
    {
      statusPanel: 'agTotalRowCountComponent',
      align: 'right',
    },
    { statusPanel: 'agFilteredRowCountComponent' },
    { statusPanel: 'agSelectedRowCountComponent' },
    { statusPanel: 'agAggregationComponent' },
  ],
};

export const PxdAgGridSideBarOptions: any = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
    },
    {
      id: 'filters',
      labelDefault: 'Filters',
      labelKey: 'filters',
      iconKey: 'filter',
      toolPanel: 'agFiltersToolPanel',
    },
  ],
};

export const PxdAgGridColumnTypes: any = {
  'nonEditableColumn': {
    editable: false
  },
  'numberColumn': {
    filter: 'agNumberColumnFilter'
  },
  'textColumn': {
    filter: 'agTextColumnFilter'
  },
  'dateColumn': {
    filter: 'agDateColumnFilter',
    valueFormatter: (date) => { return date.value ? moment(date.value).format('L') : ''; }
  }
};

export const PxdAgGridExcelStyles: any =
  [
    {
      id: 'dateISO',
      dataType: 'DateTime',
      numberFormat: {
        format: 'yyy-mm-ddThh:mm:ss'
      }
    },
    {
      id: 'numberType',
      numberFormat: {
        format: '0',
      },
    },
    {
      id: 'currencyFormat',
      numberFormat: {
        format: '$#,##0.00',
      },
    },
    {
      id: 'negativeInBrackets',
      numberFormat: {
        format: '$[blue] #,##0;$ [red](#,##0)',
      },
    },
    {
      id: 'booleanType',
      dataType: 'Boolean',
    },
    {
      id: 'stringType',
      dataType: 'String',
    },
    {
      id: 'dateType',
      dataType: 'DateTime',
      numberFormat: {
        format: 'mm/dd/yy'
      }
    },
    {
      id: 'ExcelDate',
      dataType: 'DateTime',
      numberFormat: { format: 'mm/dd/yy' } //yyy-mm-ddThh:mm:ss
    }
  ]

export interface PxdAgGridOptions extends GridOptions {
  enableCharts?: boolean;
  cellSelection?: boolean | CellSelectionOptions;
  enableGroupEdit?: boolean;
  rowGroupPanelShow?: 'always' | 'onlyWhenGrouping' | 'never';
  statusBar?: any;
  sideBar?: any;
  columnTypes?: any;
  defaultColDef?: any;
  columnDefs?: (ColDef | ColGroupDef)[] | null;
  valueCache?: boolean;
  editType?: 'fullRow';
  rowHeight?: number;
  rowSelection?: RowSelectionOptions;
  onSelectionChanged?: (event: SelectionChangedEvent) => void;
  onCellDoubleClicked?: (event: CellDoubleClickedEvent) => void;
  processCellForClipboard?(params: any): any;
  defaultExcelExportParams?: ExcelExportParams;
  excelStyles?: any;
  onCellEditingStopped?(event: CellEditingStoppedEvent);
  onCellEditingStarted?(event: CellEditingStartedEvent);
  onRowValueChanged?(event: RowValueChangedEvent);
  onRowEditingStopped?(event: RowEditingStoppedEvent);
  rowClassRules?: any;
  autoGroupColumnDef?: any;
  aggFuncs?: {};
  groupDisplayType?: RowGroupingDisplayType;
  headerHeight?: number;
  suppressAggFuncInHeader?: boolean;
  masterDetail?: boolean;
  detailCellRendererParams?: any;
  isRowMaster?: IsRowMaster;
  detailRowAutoHeight?: boolean;
  groupDefaultExpanded?: number;
  groupRowRendererParams?: any;
  groupRowRenderer?: any;
  isExternalFilterPresent?: IsExternalFilterPresent;
  doesExternalFilterPass?: DoesExternalFilterPass;
  suppressExcelExport?: boolean;
  suppressCsvExport?: boolean;
  groupMaintainOrder?: boolean;
  tooltipShowDelay?: number;
  getRowClass?: (params: RowClassParams) => string | string[] | undefined;
  isGroupOpenByDefault?: (params: any) => boolean;
  onFirstDataRendered?(event: FirstDataRenderedEvent);
  onFilterChanged?(event: FilterChangedEvent);
}

export interface PxdAgGridEvents {
  selectionChanged?: Subject<number>
  cellDoubleClicked?: Subject<number>
}

export const PxdAgGridFilterParams: any = {
  customDate: {
    comparator: function (filterLocalDateAtMidnight, cellValue) {
      //using moment js
      var dateAsString = moment(cellValue).format('MM/DD/YYYY');
      var dateParts = dateAsString.split("/");
      var cellDate = new Date(Number(dateParts[2]), Number(dateParts[0]) - 1, Number(dateParts[1]));

      if (filterLocalDateAtMidnight.getTime() == cellDate.getTime()) {
        return 0
      }

      if (cellDate < filterLocalDateAtMidnight) {
        return -1;
      }

      if (cellDate > filterLocalDateAtMidnight) {
        return 1;
      }
    },
    includeBlanksInEquals: false,
    includeBlanksInLessThan: false,
    includeBlanksInGreaterThan: false,
    includeBlanksInRange: false
  }
};

export const PXDDefaultExcelExportParams = {
  processCellCallback: params => {
    let colDef = params.column.getColDef();
    let data = params.value;
    if (colDef.cellRenderer && typeof (colDef.cellRenderer) == 'function') {
      //use the custom cell render to render to excel also
      const valueFormatterParams = {
        ...params,
        data: params.node.data,
        node: params.node!,
        colDef: params.column.getColDef()
      };
      return colDef.cellRenderer(valueFormatterParams);
    }
    else if (data && (colDef.cellClass == 'ExcelDate' || colDef.cellClass == 'dateISO' || colDef.cellClass == 'dateType')) {
      //render as a date format that excel can handle
      return moment(data).format('YYYY-MM-DD[T]hh:mm:ss'); //iso
    }
    return data;
  }
} 